import type {
  DeployedComponent,
  DeployedReleaseManifest,
} from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { Badge, DataTable, PillFilter } from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { PackageSearch } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  imageTag,
  ReleaseHeaderBand,
  RepoLink,
  shortSha,
} from '@/components/ui/ReleaseManifestDisplay';
import {
  type ComponentFamily,
  type DisplayFamily,
  displayFamily,
  FAMILY_OPTIONS,
  isComponentFamily,
} from '@/features/workspaces/componentFamily';
import { EmptyState } from './EmptyState';

function buildColumns(familyByServiceName: Record<string, string>): ColumnDef<DeployedComponent>[] {
  return [
    {
      id: 'service',
      header: 'Service',
      // Filters on service name, image tag, chart version, or commit SHA - the
      // cell below still only ever displays the service/chart name.
      accessorFn: (row) => `${row.service} ${row.dockerImage} ${row.chartVersion} ${row.commitSha}`,
      sortingFn: (rowA, rowB) => rowA.original.service.localeCompare(rowB.original.service),
      meta: { widthPercent: 40 },
      cell: ({ row }) => {
        const component = row.original;
        return (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate font-mono text-sm">{component.service}</span>
              <RepoLink gitRepo={component.gitRepo} />
            </div>
            {component.chartName && component.chartName !== component.service && (
              <p className="truncate font-mono text-xs text-muted-foreground">
                {component.chartName}
              </p>
            )}
          </div>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      enableSorting: false,
      accessorFn: (row) => displayFamily(familyByServiceName[row.service]),
      meta: { widthPercent: 10 },
      cell: ({ row }) => {
        const label = displayFamily(familyByServiceName[row.original.service]);
        return (
          <Badge variant="outline" className="text-xs">
            {label}
          </Badge>
        );
      },
    },
    {
      id: 'image',
      header: 'Image tag',
      enableSorting: false,
      accessorFn: (row) => row.dockerImage,
      meta: { widthPercent: 18 },
      cell: ({ row }) => (
        <span className="block truncate font-mono text-xs">
          {imageTag(row.original.dockerImage) || '—'}
        </span>
      ),
    },
    {
      id: 'chart',
      header: 'Chart version',
      enableSorting: false,
      accessorFn: (row) => row.chartVersion,
      meta: { widthPercent: 18 },
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.chartVersion || '—'}</span>
      ),
    },
    {
      id: 'commit',
      header: 'Commit',
      enableSorting: false,
      accessorFn: (row) => row.commitSha,
      meta: { widthPercent: 14 },
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {shortSha(row.original.commitSha) || '—'}
        </span>
      ),
    },
  ];
}

function PlatformDbRow({ component }: { component: DeployedComponent }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm">{component.service}</span>
        <span className="text-xs text-muted-foreground">platform database</span>
        <RepoLink gitRepo={component.gitRepo} />
      </div>
      <span className="font-mono text-xs text-muted-foreground">
        {shortSha(component.commitSha) || '—'}
      </span>
    </div>
  );
}

interface DeployedComponentsPanelProps {
  manifest?: DeployedReleaseManifest;
  /** Raw lowercase family values keyed by service name, straight from
   * ResolveComponentFamiliesResponse.family_by_service_name. */
  familyByServiceName?: Record<string, string>;
  /** RBAC gate for a family's empty-state deploy button - omit to render no
   * action button at all (e.g. the diff-mode call sites in
   * ComponentsDiffPanel don't pass this). */
  canDeployFamily?: (family: ComponentFamily) => boolean;
  /** Opens the deploy flow for a family, called from the empty-state action
   * button. */
  onDeployFamily?: (family: ComponentFamily) => void;
}

/**
 * Read-only view of what's actually deployed to a workspace right now, from
 * its release_metadata snapshot (captured at the last successful
 * PromoteRelease). Single-sided - no diffing, no promote controls - unlike
 * ReleaseDiffDialog which this shares its header/table styling with.
 */
export function DeployedComponentsPanel({
  manifest,
  familyByServiceName = {},
  canDeployFamily,
  onDeployFamily,
}: DeployedComponentsPanelProps) {
  const [familyFilter, setFamilyFilter] = useState<DisplayFamily | null>(null);
  const columns = useMemo(() => buildColumns(familyByServiceName), [familyByServiceName]);

  if (!manifest) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No release manifest recorded yet — this workspace hasn&rsquo;t completed a promote since
        release tracking was added.
      </p>
    );
  }

  const filteredComponents = familyFilter
    ? manifest.components.filter(
        (c) => displayFamily(familyByServiceName[c.service]) === familyFilter,
      )
    : manifest.components;

  const deployableFamilyFilter = isComponentFamily(familyFilter) ? familyFilter : null;
  const canDeploy =
    deployableFamilyFilter &&
    deployableFamilyFilter !== 'A8' &&
    canDeployFamily?.(deployableFamilyFilter);

  const familyFilterSlot = manifest.components.length > 0 && (
    <PillFilter
      label="Type"
      value={familyFilter}
      onChange={(v) => setFamilyFilter(v as DisplayFamily | null)}
      options={FAMILY_OPTIONS}
    />
  );

  return (
    <div className="space-y-4">
      <ReleaseHeaderBand release={manifest.releaseInfo} />

      {manifest.platformDb && <PlatformDbRow component={manifest.platformDb} />}

      <DataTable
        columns={columns}
        data={filteredComponents}
        searchKey="service"
        searchPlaceholder="Search components…"
        filterSlot={familyFilterSlot}
        pageSize={1000}
        enableSorting
        initialSorting={[{ id: 'service', desc: false }]}
        emptyState={
          familyFilter && (
            <EmptyState
              icon={<PackageSearch className="h-6 w-6 text-muted-foreground" />}
              title={`No ${familyFilter} components deployed`}
              description={`This workspace has no ${familyFilter} components deployed yet.`}
              actionLabel={canDeploy ? `Deploy ${familyFilter}` : undefined}
              onAction={
                canDeploy
                  ? () => deployableFamilyFilter && onDeployFamily?.(deployableFamilyFilter)
                  : undefined
              }
              className="py-8"
            />
          )
        }
      />
    </div>
  );
}
